import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { activeMention } from '../../lib/mentions';
import { Bold, Italic, List, ListOrdered, ImagePlus, X, Loader2 } from 'lucide-react';
import FormattedText from './FormattedText';
import { matchListLine, highlightSegments } from '../../lib/formatText';
import { MAX_IMAGES, uploadPostImage, imageErrorMessage, cachedSignedUrl, signImageUrls } from '../../lib/postImages';

// The one body editor for every place a member writes a post or reply:
// CreatePostModal, the inline form on a table's detail screen, the reply box,
// and editing a post or a reply. Those were five separate <textarea>s with
// their own sizing and counters; features added here reach all of them.
//
// Stores plain text with the formatting subset from src/lib/formatText.js.
// The toolbar only inserts that syntax — it is a shortcut, not a mode, so what
// you see in Write is exactly what gets saved.
//
// Text is 15px to match the rendered post (CLAUDE.md: inline editors match the
// prose they replace, so nothing reflows when you start editing).

// ── Formatting shown while typing ─────────────────────────────
// A textarea can't style part of its text, so the styled copy is drawn in a
// layer BEHIND a textarea whose own glyphs are transparent (index.css
// `.post-editor-input`). The caret, selection, IME, autocorrect and native
// iOS editing all stay the textarea's — nothing about input changes.
//
// ⚠️ The layer lines up with the caret only while every glyph has the same
// advance width in both. So bold is FAKED with a hairline text-shadow (a real
// bold face is wider and the caret would drift along the line), and italic is
// a soft tint rather than slanted type, for the same reason. Don't "fix" either
// into font-weight / font-style. Anything that changes width — padding,
// letter-spacing, font-size — must be identical on both elements.
const segmentStyle = (seg) => ({
  color: seg.marker
    ? 'var(--text-tertiary)'
    : seg.link || seg.mention ? 'var(--brand-primary)' : undefined,
  textShadow: seg.bold && !seg.marker
    ? '0.35px 0 0 currentColor, -0.35px 0 0 currentColor'
    : undefined,
  backgroundColor: seg.italic && !seg.marker
    ? 'rgba(var(--brand-secondary-rgb), 0.18)'
    : undefined,
  borderRadius: seg.italic && !seg.marker ? 2 : undefined,
});

// Shared by the textarea and the layer so the two can't drift apart.
const TEXT_CLASS = 'px-3 py-2.5 text-[15px] leading-relaxed whitespace-pre-wrap [overflow-wrap:break-word] [word-break:normal]';

// ── @mention suggestions ───────────────────────────────────
// Typing @ and a letter lists matching members (search_members_for_mention,
// 053 — all members, by the user's choice; see the migration header). Picking
// one writes "@username " into the text; the server works out who to notify
// from the saved text, so nothing else is sent.
const MENTION_DEBOUNCE_MS = 150;

const SuggestionAvatar = ({ member }) => (
  member.avatar_url ? (
    <img src={member.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
  ) : (
    <div className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center text-white text-xs font-bold bg-gradient-to-br from-brand-primary to-brand-secondary">
      {(member.display_name || member.username || '?')[0].toUpperCase()}
    </div>
  )
);

const SIZES = {
  post: { minRows: 5, maxHeight: 420 },
  reply: { minRows: 3, maxHeight: 320 },
};

// Attached photos are owned by the caller (they have to be saved against the
// post id, which doesn't exist yet at compose time), so the editor takes the
// list and hands back changes. `userId` non-null is what turns the feature on.
const PostEditor = ({
  value,
  onChange,
  maxLength,
  placeholder,
  onSubmit,
  autoFocus = false,
  size = 'post',
  ariaLabel,
  images = null,          // null = this surface has no photo support
  onImagesChange,
  userId = null,
}) => {
  const ref = useRef(null);
  const layerRef = useRef(null);
  const fileRef = useRef(null);
  const pendingSelection = useRef(null);
  const [preview, setPreview] = useState(false);
  const [uploading, setUploading] = useState(0);
  const [imageError, setImageError] = useState('');
  const [thumbUrls, setThumbUrls] = useState({});
  const [mention, setMention] = useState(null);        // { start, query } at the caret
  const [suggestions, setSuggestions] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [dismissedAt, setDismissedAt] = useState(null); // Escape hides it until a new @
  const photosOn = !!images && !!userId && typeof onImagesChange === 'function';
  const imageList = images ?? [];
  const { minRows, maxHeight } = SIZES[size] ?? SIZES.post;
  const listId = useId();

  // Grow with the content, then scroll. Reset to auto first or it never shrinks.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
    syncLayer();
  }, [value, preview, maxHeight]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Keeps the styled layer under the textarea's text: same scroll offset, and
  // the same wrap width once a classic (space-taking) scrollbar appears — on
  // desktop Windows that is ~15px, which would otherwise wrap lines differently.
  const syncLayer = () => {
    const el = ref.current;
    const layer = layerRef.current;
    if (!el || !layer) return;
    layer.style.transform = `translateY(${-el.scrollTop}px)`;
    layer.style.marginRight = `${el.offsetWidth - el.clientWidth}px`;
  };

  // Toolbar edits change `value` through the parent; the caret can only be put
  // back once that render has landed.
  useLayoutEffect(() => {
    const sel = pendingSelection.current;
    const el = ref.current;
    if (!sel || !el) return;
    pendingSelection.current = null;
    el.focus();
    el.setSelectionRange(sel[0], sel[1]);
  }, [value]);

  useEffect(() => {
    if (!preview && autoFocus) ref.current?.focus();
    // Returning from Preview puts you back where you were typing.
  }, [preview, autoFocus]);

  // Re-read on every caret move as well as every keystroke: tapping into the
  // middle of an existing @name should offer suggestions for it too.
  const trackMention = (el) => {
    if (!el) return;
    const found = el.selectionStart === el.selectionEnd
      ? activeMention(el.value, el.selectionStart)
      : null;
    setMention((prev) => (
      prev?.start === found?.start && prev?.query === found?.query ? prev : found
    ));
  };

  useEffect(() => {
    const query = mention?.query ?? '';
    if (!query || dismissedAt === mention.start) { setSuggestions([]); return undefined; }
    let cancelled = false;
    const timer = setTimeout(async () => {
      const { data, error } = await supabase.rpc('search_members_for_mention', { p_query: query });
      if (cancelled) return;
      if (error) console.error('search_members_for_mention:', error);
      setSuggestions(data ?? []);
      setActiveIdx(0);
    }, MENTION_DEBOUNCE_MS);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [mention, dismissedAt]);

  const showSuggestions = !preview && !!mention && suggestions.length > 0;

  const chooseMention = (member) => {
    const el = ref.current;
    if (!el || !mention) return;
    const caret = el.selectionStart;
    const insert = `@${member.username} `;
    const next = value.slice(0, mention.start) + insert + value.slice(caret);
    setMention(null);
    setSuggestions([]);
    apply(next, mention.start + insert.length, mention.start + insert.length);
  };

  const apply = (next, selStart, selEnd) => {
    if (maxLength && next.length > maxLength) return;
    pendingSelection.current = [selStart, selEnd];
    onChange(next);
  };

  // Bold / italic: wrap the selection, unwrap if it is already wrapped, or
  // drop an empty pair with the caret between. Surrounding spaces are left
  // outside the markers — "**word **" would not parse as bold.
  const toggleWrap = (marker) => {
    const el = ref.current;
    if (!el) return;
    let start = el.selectionStart;
    let end = el.selectionEnd;
    while (start < end && /\s/.test(value[start])) start++;
    while (end > start && /\s/.test(value[end - 1])) end--;
    const m = marker.length;

    if (value.slice(start - m, start) === marker && value.slice(end, end + m) === marker) {
      apply(value.slice(0, start - m) + value.slice(start, end) + value.slice(end + m), start - m, end - m);
      return;
    }
    const selected = value.slice(start, end);
    apply(value.slice(0, start) + marker + selected + marker + value.slice(end), start + m, end + m);
  };

  // Bullets / numbers: applied to every line the selection touches. If they
  // are all already that kind of list, the prefixes come off instead.
  const toggleList = (kind) => {
    const el = ref.current;
    if (!el) return;
    const lineStart = value.lastIndexOf('\n', el.selectionStart - 1) + 1;
    const nextBreak = value.indexOf('\n', el.selectionEnd);
    const lineEnd = nextBreak === -1 ? value.length : nextBreak;
    const lines = value.slice(lineStart, lineEnd).split('\n');
    const allThisKind = lines.every(l => !l.trim() || matchListLine(l)?.kind === kind);

    let n = 0;
    const replaced = lines.map(line => {
      const bare = matchListLine(line)?.content ?? line;
      if (allThisKind) return bare;
      if (!bare.trim() && lines.length > 1) return bare;
      n += 1;
      return kind === 'ol' ? `${n}. ${bare}` : `- ${bare}`;
    }).join('\n');

    const next = value.slice(0, lineStart) + replaced + value.slice(lineEnd);
    const caret = lineStart + replaced.length;
    apply(next, caret, caret);
  };

  const handleKeyDown = (e) => {
    if (e.nativeEvent.isComposing) return;

    // The suggestion list owns the arrow keys, Enter, Tab and Escape while it
    // is open. stopPropagation keeps Escape from also closing the modal.
    if (showSuggestions) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const step = e.key === 'ArrowDown' ? 1 : -1;
        setActiveIdx((i) => (i + step + suggestions.length) % suggestions.length);
        return;
      }
      if ((e.key === 'Enter' && !e.metaKey && !e.ctrlKey && !e.shiftKey) || e.key === 'Tab') {
        e.preventDefault();
        chooseMention(suggestions[activeIdx] ?? suggestions[0]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setDismissedAt(mention.start);
        return;
      }
    }

    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      if (onSubmit) { e.preventDefault(); onSubmit(); }
      return;
    }
    if (e.key === 'b' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); toggleWrap('**'); return; }
    if (e.key === 'i' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); toggleWrap('_'); return; }

    // Enter inside a list item continues the list; Enter on an empty item ends it.
    if (e.key === 'Enter' && !e.shiftKey) {
      const el = e.currentTarget;
      if (el.selectionStart !== el.selectionEnd) return;
      const caret = el.selectionStart;
      const lineStart = value.lastIndexOf('\n', caret - 1) + 1;
      const item = matchListLine(value.slice(lineStart, caret));
      if (!item) return;
      e.preventDefault();
      if (!item.content.trim()) {
        apply(value.slice(0, lineStart) + value.slice(caret), lineStart, lineStart);
        return;
      }
      const prefix = item.kind === 'ol' ? `${item.number + 1}. ` : '- ';
      const insert = `\n${prefix}`;
      apply(value.slice(0, caret) + insert + value.slice(caret), caret + insert.length, caret + insert.length);
    }
  };

  // Freshly uploaded thumbnails need signed URLs like any other (private bucket).
  useEffect(() => {
    const paths = imageList.map((i) => i.thumb).filter((p) => p && !cachedSignedUrl(p));
    if (!paths.length) return;
    let cancelled = false;
    signImageUrls(paths).then((map) => { if (!cancelled) setThumbUrls((prev) => ({ ...prev, ...map })); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageList.map((i) => i.thumb).join('|')]);

  const handleFiles = async (e) => {
    const files = [...(e.target.files ?? [])];
    e.target.value = '';
    if (!files.length) return;
    setImageError('');

    const room = MAX_IMAGES - imageList.length;
    if (room <= 0) { setImageError(`Up to ${MAX_IMAGES} photos.`); return; }
    const batch = files.slice(0, room);
    if (files.length > room) setImageError(`Only ${room} more photo${room === 1 ? '' : 's'} fit.`);

    setUploading((n) => n + batch.length);
    for (const file of batch) {
      // Sequential: a phone uploading four photos at once on a weak connection
      // is how you get four timeouts instead of two photos.
      // eslint-disable-next-line no-await-in-loop
      const { image, error } = await uploadPostImage(userId, file);
      setUploading((n) => n - 1);
      if (error) { setImageError(imageErrorMessage(error)); continue; }
      onImagesChange([...(images ?? []), image].slice(0, MAX_IMAGES));
    }
  };

  // Removing here only drops it from the draft. The file is cleaned up by the
  // deletion sweep once the row goes, or stays an unattached orphan if the
  // post is never published — the documented cleanup case in 041.
  const removeImage = (path) => {
    setImageError('');
    onImagesChange(imageList.filter((i) => i.path !== path));
  };

  const nearLimit = maxLength && value.length >= maxLength * 0.8;

  const toolButton = (label, Icon, onClick) => (
    <button
      type="button"
      // mousedown default would move focus off the textarea and lose the selection
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      aria-label={label}
      title={label}
      disabled={preview}
      className="flex items-center justify-center w-11 h-11 md:w-8 md:h-8 rounded-lg hover-surface transition-all active:scale-95 disabled:opacity-40"
      style={{ color: 'var(--text-secondary)' }}
    >
      <Icon size={16} />
    </button>
  );

  const tabButton = (label, active, onClick) => (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className="px-2.5 py-1 rounded-md text-xs font-medium transition-all"
      style={{
        backgroundColor: active ? 'var(--card-bg)' : 'transparent',
        color: active ? 'var(--text-primary)' : 'var(--text-tertiary)',
        boxShadow: active ? '0 1px 2px rgba(0,0,0,0.06)' : undefined,
      }}
    >
      {label}
    </button>
  );

  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)' }}
    >
      <div
        className="flex items-center gap-0.5 px-1.5 py-1"
        style={{ borderBottom: '1px solid var(--input-border)' }}
        role="toolbar"
        aria-label="Formatting"
      >
        {toolButton('Bold', Bold, () => toggleWrap('**'))}
        {toolButton('Italic', Italic, () => toggleWrap('_'))}
        {toolButton('Bulleted list', List, () => toggleList('ul'))}
        {toolButton('Numbered list', ListOrdered, () => toggleList('ol'))}
        {photosOn && toolButton('Add photos', ImagePlus, () => fileRef.current?.click())}
        <div className="ml-auto flex items-center rounded-lg p-0.5" style={{ backgroundColor: 'var(--pill-bg)' }}>
          {tabButton('Write', !preview, () => setPreview(false))}
          {tabButton('Preview', preview, () => setPreview(true))}
        </div>
      </div>

      {preview ? (
        <div className="px-3 py-2.5 text-[15px] leading-relaxed text-secondary" style={{ minHeight: `${minRows * 1.6 + 1.25}rem` }}>
          {value.trim()
            ? <FormattedText text={value} />
            : <p style={{ color: 'var(--text-tertiary)' }}>Nothing to preview yet.</p>}
        </div>
      ) : (
        <div className="relative">
          <div aria-hidden="true" className="absolute inset-0 overflow-hidden pointer-events-none">
            <div ref={layerRef} className={`${TEXT_CLASS} text-primary`}>
              {highlightSegments(value).map((seg, i) => (
                <span key={i} style={segmentStyle(seg)}>{seg.text}</span>
              ))}
              {/* A trailing newline collapses in a div but not in a textarea. */}
              {value.endsWith('\n') ? ' ' : null}
            </div>
          </div>
          <textarea
            ref={ref}
            value={value}
            onChange={(e) => {
              onChange(maxLength ? e.target.value.slice(0, maxLength) : e.target.value);
              trackMention(e.target);
            }}
            onSelect={(e) => trackMention(e.currentTarget)}
            // Delayed so a tap on a suggestion lands before the list closes.
            onBlur={() => setTimeout(() => setMention(null), 150)}
            onKeyDown={handleKeyDown}
            aria-autocomplete="list"
            aria-controls={showSuggestions ? listId : undefined}
            aria-activedescendant={showSuggestions ? `${listId}-${activeIdx}` : undefined}
            onScroll={syncLayer}
            placeholder={placeholder}
            aria-label={ariaLabel || placeholder}
            rows={minRows}
            autoFocus={autoFocus}
            spellCheck
            className={`post-editor-input relative block w-full bg-transparent resize-none focus:outline-none ${TEXT_CLASS}`}
          />
        </div>
      )}

      {showSuggestions && (
        <ul
          id={listId}
          role="listbox"
          aria-label="Mention a member"
          className="py-1"
          style={{ borderTop: '1px solid var(--input-border)' }}
        >
          {suggestions.map((member, i) => (
            <li
              key={member.id}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === activeIdx}
              // mousedown default would blur the textarea and lose the caret
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => chooseMention(member)}
              onMouseEnter={() => setActiveIdx(i)}
              className="flex items-center gap-2.5 px-3 py-2 min-h-[44px] md:min-h-0 cursor-pointer"
              style={{ backgroundColor: i === activeIdx ? 'var(--pill-bg)' : 'transparent' }}
            >
              <SuggestionAvatar member={member} />
              <span className="min-w-0 truncate text-sm">
                <span className="font-medium text-primary">{member.display_name}</span>
                {member.display_name !== member.username && (
                  <span style={{ color: 'var(--text-tertiary)' }}> @{member.username}</span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}

      {photosOn && (
        <>
          <input
            ref={fileRef}
            type="file"
            // image/* so iOS offers the camera and converts HEIC on pick
            accept="image/*"
            multiple
            onChange={handleFiles}
            className="hidden"
          />
          {(imageList.length > 0 || uploading > 0 || imageError) && (
            <div className="px-3 pt-2 pb-1">
              <div className="flex flex-wrap gap-2">
                {imageList.map((image) => (
                  <div
                    key={image.path}
                    className="relative w-16 h-16 rounded-lg overflow-hidden"
                    style={{ backgroundColor: 'var(--skeleton-bg)' }}
                  >
                    {thumbUrls[image.thumb] || cachedSignedUrl(image.thumb) ? (
                      <img
                        src={thumbUrls[image.thumb] || cachedSignedUrl(image.thumb)}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : null}
                    <button
                      type="button"
                      onClick={() => removeImage(image.path)}
                      aria-label="Remove photo"
                      className="absolute top-0.5 right-0.5 p-0.5 rounded-full"
                      style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
                    >
                      <X size={12} className="text-white" />
                    </button>
                  </div>
                ))}
                {Array.from({ length: uploading }).map((_, i) => (
                  <div
                    key={`up-${i}`}
                    className="w-16 h-16 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: 'var(--skeleton-bg)' }}
                  >
                    <Loader2 size={16} className="animate-spin" style={{ color: 'var(--text-tertiary)' }} />
                  </div>
                ))}
              </div>
              {/* Said once, where the photos are chosen. These are workplaces
                  and the people members support. */}
              {(imageList.length > 0 || uploading > 0) && (
                <p className="text-[11px] mt-1.5 leading-snug" style={{ color: 'var(--text-tertiary)' }}>
                  Please don't post photos that identify the people you support. Location data is removed automatically.
                </p>
              )}
              {imageError && (
                <p className="text-xs mt-1" style={{ color: 'var(--color-error)' }}>{imageError}</p>
              )}
            </div>
          )}
        </>
      )}

      {nearLimit && (
        <p
          className="px-3 pb-1.5 text-xs text-right"
          style={{ color: value.length >= maxLength ? 'var(--color-error)' : 'var(--text-tertiary)' }}
        >
          {value.length}/{maxLength}
        </p>
      )}
    </div>
  );
};

export default PostEditor;
